import Widget from 'flarum/extensions/afrux-forum-widgets-core/common/components/Widget';
import app from 'flarum/common/app';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import CreateGroupModal from './CreateGroupModal';

export default class UserGroupsWidget extends Widget {
  oninit(vnode) {
    super.oninit(vnode);
    
    this.allGroups = []; // 存储所有群组数据
    this.groups = []; // 当前页面显示的群组
    this.isLoading = true;
    this.error = null;
    this.totalGroups = 0;
    this.currentPage = 1;
    this.totalPages = 1;
    
    // 检测是否为移动设备（屏幕宽度小于 768px）
    this.updateCardsPerPage();
    
    this.isHovering = false;
    this.resizeHandler = null; // 窗口大小变化处理器
    
    // 触摸手势相关状态
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    this.minSwipeDistance = 50; // 最小滑动距离（像素）
    this.touchHandler = null; // 触摸事件处理器
    
    // 预先加载所有群组数据
    this.loadAllGroups();
  }

  oncreate(vnode) {
    super.oncreate(vnode);
    
    // 添加窗口大小变化监听器（使用防抖优化性能）
    let resizeTimeout;
    this.resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const oldCardsPerPage = this.cardsPerPage;
        this.updateCardsPerPage();
        
        // 如果每页卡片数发生变化，需要重新计算和更新
        if (oldCardsPerPage !== this.cardsPerPage) {
          this.recalculatePages();
        }
      }, 150); // 150ms 防抖延迟
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.resizeHandler);
    }
    
    // 添加触摸手势支持
    this.setupTouchHandlers(vnode);
  }

  onremove(vnode) {
    super.onremove(vnode);
    
    // 移除窗口大小变化监听器
    if (this.resizeHandler && typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    
    // 移除触摸事件监听器
    this.removeTouchHandlers(vnode);
  }

  updateCardsPerPage() {
    // 检测是否为移动设备（屏幕宽度小于 768px）
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    this.cardsPerPage = isMobile ? 1 : 3; // 移动端显示1个，桌面端显示3个
  }

  recalculatePages() {
    // 重新计算总页数
    if (this.allGroups && this.allGroups.length > 0) {
      const newTotalPages = Math.ceil(this.allGroups.length / this.cardsPerPage) || 1;
      
      // 如果当前页超出新的总页数，调整到最后一页
      if (this.currentPage > newTotalPages) {
        this.currentPage = newTotalPages || 1;
      }
      
      this.totalPages = newTotalPages;
      
      // 更新显示的群组
      this.updateDisplayedGroups();
      
      // 触发重新渲染
      m.redraw();
    }
  }

  async loadAllGroups() {
    this.isLoading = true;
    this.error = null;
    
    try {
      // 优化：只加载足够的数据用于 Widget 显示
      // Widget 每页显示 3 个卡片（桌面端）或 1 个卡片（移动端），加载 10 条数据
      // 如果用户需要更多，可以点击 "View all" 跳转到完整页面
      const response = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/social-groups/frontend',
        headers: {
          'Content-Type': 'application/vnd.api+json',
        },
        params: {
          page: 1,
          limit: 10 // 默认只加载 10 条数据
        }
      });
      
      if (response && response.data) {
        this.totalGroups = response.meta?.total || 0;
        // 优化：直接使用 API 返回的数据，避免额外的详情请求
        // 消除 N+1 查询问题
        this.allGroups = response.data.map((group) => {
          // 如果 API 已经返回了成员信息，使用它；否则使用空数组
          const members = group.members || [];
          
          return {
            id: group.id,
            name: group.name || '未命名群组',
            description: group.description || '',
            memberCount: group.member_count || 0,
            coverImage: group.cover_image || null,
            privacy: group.privacy || 'public',
            members: members.slice(0, 5), // 只取前5个成员用于头像显示
            commentCount: group.comment_count || group.stats?.replies || 0,
            userStatus: group.user_status || null, // 保存用户状态信息
          };
        });
        
        // 基于实际加载的数据计算总页数（每页显示cardsPerPage个）
        this.totalPages = Math.ceil(this.allGroups.length / this.cardsPerPage) || 1;
        
        
        // 设置当前页面显示的数据
        this.updateDisplayedGroups();
      } else {
        this.allGroups = [];
        this.groups = [];
      }
    } catch (error) {
      console.error('Error loading social groups:', error);
      this.error = error.message || 'Failed to load social groups';
      this.allGroups = [];
      this.groups = [];
    } finally {
      this.isLoading = false;
      m.redraw();
    }
  }

  updateDisplayedGroups() {
    // 从所有群组中获取当前页应该显示的数据
    const startIndex = (this.currentPage - 1) * this.cardsPerPage;
    const endIndex = startIndex + this.cardsPerPage;
    this.groups = this.allGroups.slice(startIndex, endIndex);
  }

  slideLeft() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updateDisplayedGroups();
      m.redraw();
    }
  }

  slideRight() {
    
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updateDisplayedGroups();
      m.redraw();
    }
  }

  /**
   * 设置触摸手势处理器
   */
  setupTouchHandlers(vnode) {
    // 使用 setTimeout 确保 DOM 已经渲染完成
    setTimeout(() => {
      const container = vnode.dom?.querySelector('.UserGroupsWidget-cards');
      if (!container) {
        // 如果找不到容器，尝试从根元素查找
        const root = vnode.dom?.querySelector('.UserGroupsWidget');
        if (root) {
          const cards = root.querySelector('.UserGroupsWidget-cards');
          if (cards) {
            this.attachTouchHandlers(cards);
          }
        }
        return;
      }
      
      this.attachTouchHandlers(container);
    }, 0);
  }

  /**
   * 将触摸事件处理器附加到容器元素
   */
  attachTouchHandlers(container) {
    // 触摸开始
    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
    };

    // 触摸结束
    const handleTouchEnd = (e) => {
      if (!this.touchStartX || !this.touchStartY) return;

      const touch = e.changedTouches[0];
      this.touchEndX = touch.clientX;
      this.touchEndY = touch.clientY;

      this.handleSwipe();
      
      // 重置触摸状态
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchEndX = 0;
      this.touchEndY = 0;
    };

    // 触摸移动（可选：用于实时反馈）
    const handleTouchMove = (e) => {
      // 可以在这里添加滑动时的视觉反馈
      // 目前只记录位置，不阻止默认行为
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });

    // 保存处理器引用以便后续移除
    this.touchHandler = {
      container,
      handleTouchStart,
      handleTouchEnd,
      handleTouchMove
    };
  }

  /**
   * 移除触摸手势处理器
   */
  removeTouchHandlers(vnode) {
    if (!this.touchHandler) return;

    const { container, handleTouchStart, handleTouchEnd, handleTouchMove } = this.touchHandler;
    
    if (container) {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchmove', handleTouchMove);
    }

    this.touchHandler = null;
  }

  /**
   * 处理滑动手势
   */
  handleSwipe() {
    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = this.touchEndY - this.touchStartY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // 判断是否为水平滑动（水平距离大于垂直距离，且水平距离超过最小阈值）
    if (absDeltaX > absDeltaY && absDeltaX > this.minSwipeDistance) {
      if (deltaX > 0) {
        // 向右滑动，显示上一页
        this.slideLeft();
      } else {
        // 向左滑动，显示下一页
        this.slideRight();
      }
    }
  }

  className() {
    return 'tf-groups-user-groups-widget';
  }

  icon() {
    return 'fa-light fa-user-group';
  }

  title() {
    return '';
  }

  description() {
    return '';
  }

  viewGroup(group) {
    if (group && group.id) {
      m.route.set(app.route('groupDetail', { id: group.id }));
    }
  }

  showCreateGroupModal() {
    app.modal.show(CreateGroupModal);
  }

  content() {
    if (this.isLoading) {
      return (
        <div className="UserGroupsWidget-loading">
          <LoadingIndicator />
        </div>
      );
    }

    if (this.error) {
      return (
        <div className="UserGroupsWidget-error">
          <p>{this.error}</p>
        </div>
      );
    }

    if (!this.allGroups || this.allGroups.length === 0) {
      return (
        <div className="UserGroupsWidget-empty">
          <p>暂无群组</p>
        </div>
      );
    }

    const canSlideLeft = this.currentPage > 1;
    const canSlideRight = this.currentPage < this.totalPages;


    return (
      <div 
        className="UserGroupsWidget"
        onmouseenter={() => {
          this.isHovering = true;
          m.redraw();
        }}
        onmouseleave={() => {
          this.isHovering = false;
          m.redraw();
        }}
      >
        <div className="UserGroupsWidget-header">
          <h2 className="UserGroupsWidget-title">User Groups</h2>
          {app.session.user && (
            <button
              className="UserGroupsWidget-applyButton"
              onclick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                this.showCreateGroupModal();
              }}
              type="button"
            >
              <i className="fa-light fa-plus"></i> Apply for New User Group
            </button>
          )}
          {this.totalGroups > 0 && (
            <div className="UserGroupsWidget-viewAll">
              <button
                className="UserGroupsWidget-viewAll-arrow"
                onclick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (canSlideLeft) {
                    this.slideLeft();
                  }
                }}
                disabled={!canSlideLeft}
                type="button"
              >
                <i className="fa-light fa-chevron-left"></i>
              </button>
              <a 
                href={app.route('groups')}
                onclick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  m.route.set(app.route('groups'));
                }}
              >
                View all {this.totalGroups} groups
              </a>
              <button
                className="UserGroupsWidget-viewAll-arrow"
                onclick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (canSlideRight) {
                    this.slideRight();
                  }
                }}
                disabled={!canSlideRight}
                type="button"
              >
                <i className="fa-light fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
        
        <div className="UserGroupsWidget-container">
          {/* 左滑动按钮 */}
          {/* <button
            className={`UserGroupsWidget-navButton UserGroupsWidget-navButton--left ${!canSlideLeft ? 'is-disabled' : ''} ${this.isHovering ? 'is-visible' : ''}`}
            onclick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (canSlideLeft) {
                this.slideLeft();
              }
            }}
            type="button"
            aria-disabled={!canSlideLeft}
          >
            <i className="fa-light fa-chevron-left"></i>
          </button> */}

          <div 
            className="UserGroupsWidget-cards"
            ontouchstart={(e) => {
              const touch = e.touches[0];
              if (touch) {
                this.touchStartX = touch.clientX;
                this.touchStartY = touch.clientY;
              }
            }}
            ontouchend={(e) => {
              if (!this.touchStartX || !this.touchStartY) return;
              
              const touch = e.changedTouches[0];
              if (touch) {
                this.touchEndX = touch.clientX;
                this.touchEndY = touch.clientY;
                this.handleSwipe();
                
                // 重置触摸状态
                this.touchStartX = 0;
                this.touchStartY = 0;
                this.touchEndX = 0;
                this.touchEndY = 0;
              }
            }}
          >
            {this.groups.map((group, index) => this.renderGroupCard(group, index))}
          </div>

          {/* 右滑动按钮 */}
          {/* <button
            className={`UserGroupsWidget-navButton UserGroupsWidget-navButton--right ${!canSlideRight ? 'is-disabled' : ''} ${this.isHovering ? 'is-visible' : ''}`}
            onclick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (canSlideRight) {
                this.slideRight();
              }
            }}
            type="button"
            aria-disabled={!canSlideRight}
          >
            <i className="fa-light fa-chevron-right"></i>
          </button> */}
        </div>
        
        {/* 分页指示器 */}
        {this.totalPages > 1 && (
          <div className="UserGroupsWidget-pagination">
            {Array.from({ length: this.totalPages }).map((_, index) => (
              <div
                key={index}
                className={`UserGroupsWidget-pagination-dot ${index + 1 === this.currentPage ? 'is-active' : ''}`}
                onclick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.currentPage = index + 1;
                  this.updateDisplayedGroups();
                  m.redraw();
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  renderGroupCard(group, index) {
    const coverImageUrl = group.coverImage 
      ? (group.coverImage.startsWith('http') ? group.coverImage : app.forum.attribute('baseUrl') + '/' + group.coverImage)
      : null;

    return (
      <div 
        className="UserGroupsWidget-card"
        key={group.id}
        onclick={() => this.viewGroup(group)}
      >
        {/* 插图区域 - 顶部圆角 */}
        <div className="UserGroupsWidget-card-illustration" style={coverImageUrl ? {
          backgroundImage: `url(${coverImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : {}}>
          {!coverImageUrl && (
            <div className="UserGroupsWidget-card-illustration-placeholder">
              <i className="fa-light fa-users"></i>
            </div>
          )}
        </div>
        
        {/* 内容区域 - 白色背景 */}
        <div className="UserGroupsWidget-card-content">
          <div className="UserGroupsWidget-card-title-wrapper">
            <h3 className="UserGroupsWidget-card-title">{group.name}</h3>
            {/* 用户状态标签 */}
            {group.userStatus && this.renderUserStatusTag(group.userStatus)}
          </div>
          
          {/* 描述文字 */}
          {group.description && (
            <div className="UserGroupsWidget-card-description">
              {group.description}
            </div>
          )}
          
          <div className="UserGroupsWidget-card-footer">
            {/* 用户头像 */}
            <div className="UserGroupsWidget-card-avatars">
              {group.members && group.members.length > 0 ? (
                group.members.map((member, idx) => {
                  // 处理成员数据格式（可能是对象或用户对象）
                  const user = member.user || member;
                  const userObj = user && typeof user === 'object' ? user : {
                    id: member.id || idx,
                    username: member.username || 'user',
                    displayName: member.display_name || member.username || 'User',
                    avatarUrl: member.avatar_url || null
                  };
                  
                  return (
                    <div 
                      key={userObj.id || idx} 
                      className="UserGroupsWidget-card-avatar"
                      style={{ zIndex: 10 - idx }}
                    >
                      {userObj.avatarUrl ? (
                        <img src={userObj.avatarUrl} alt={userObj.displayName || userObj.username} />
                      ) : (
                        <div className="UserGroupsWidget-card-avatar-placeholder">
                          {(userObj.displayName || userObj.username || 'U').charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                // 如果没有成员数据，显示占位头像
                Array.from({ length: Math.min(group.memberCount || 0, 5) }).map((_, idx) => (
                  <div 
                    key={idx} 
                    className="UserGroupsWidget-card-avatar UserGroupsWidget-card-avatar-placeholder"
                    style={{ zIndex: 10 - idx }}
                  >
                    <i className="fa-light fa-user"></i>
                  </div>
                ))
              )}
            </div>
            
            {/* 指标 */}
            <div className="UserGroupsWidget-card-metrics">
              <div className="UserGroupsWidget-card-metric">
                <i className="fa-light fa-user-group"></i>
                <span>{group.memberCount || 0}</span>
              </div>
              <div className="UserGroupsWidget-card-metric">
                <i className="fa-light fa-comments"></i>
                <span>{group.commentCount || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * 根据用户状态渲染标签
   */
  renderUserStatusTag(userStatus) {
    if (!userStatus) {
      return null;
    }

    // 判断是否为组员：role 为 "member" 且 status 为 "approved"
    const isMember = userStatus.role === 'member' && userStatus.status === 'approved';
    
    // 判断是否为版主/管理员：role 为 "admin" 或 "moderator"
    const isModerator = userStatus.role === 'admin';

    if (isMember) {
      return (
        <span className="UserGroupsWidget-card-tag UserGroupsWidget-card-tag--joined">
          Joined
        </span>
      );
    }

    if (isModerator) {
      return (
        <span className="UserGroupsWidget-card-tag UserGroupsWidget-card-tag--moderator">
          Moderator
        </span>
      );
    }

    return null;
  }
}

